import BigNumber from "bignumber.js";
import { BASE18, DilutionRank, TimeLockPercentage } from "src/configs/constance";
import DilutionStakingContract from "src/contract-logic/logic/DilutionStakingContract";
import { collectionSort, isNullAddress } from "src/utils";
import multiCall from "src/utils/multicall";
import CollectionQuery from "./collection-query";

export default class DilutionStakingQuery {
  static async fetchArmyList(armyIds) {
    const [battleInfo] = await Promise.all([
      multiCall(
        DilutionStakingContract.ABI,
        armyIds.map((armyId) => ({
          address: DilutionStakingContract.ADDRESS,
          name: "getArmyInfo",
          params: [armyId],
        }))
      ),
    ]);
    const result = {};
    let counter = 0;
    let collectionIds = [];
    for (const _battle of battleInfo) {
      const battle = _battle[0];
      const _id = parseInt(armyIds[counter]);
      const stakedKnightId = battle.stakedKnight.map((item) => parseInt(item));
      collectionIds = collectionIds.concat(stakedKnightId);
      const armyRank = parseInt(battle.armyRank);
      const type = DilutionRank[armyRank];
      const dilution = parseInt(battle.dilution._hex) / 10000;
      const metadata = TimeLockPercentage.find((item) => item.percent == dilution) ?? TimeLockPercentage[0];
      const unlockTime = parseInt(battle.unlockTime);
      const lockTime = unlockTime - metadata["date"];
      const lockAmount = parseInt(BigNumber(battle.lockAmount._hex).div(BASE18));
      result[_id.toString()] = {
        armyRank,
        type,
        metadata,
        dilution,
        lockAmount,
        reward: lockAmount * metadata["percent"],
        lockCirculatingSupply: parseInt(BigNumber(battle.lockCirculatingSupply._hex).div(BASE18)),
        unlockCirculatingSupply: parseInt(BigNumber(battle.unlockCirculatingSupply._hex).div(BASE18)),
        lockTime,
        unlockTime,
        id: _id,
      };
      counter++;
    }
    const { normalCollections: collectionDataList } = await CollectionQuery.fetchBasicCollections(collectionIds);
    let armorTokenIdArray = [];
    let helmetTokenIdArray = [];
    let shieldTokenIdArray = [];
    let weaponTokenIdArray = [];
    collectionDataList.forEach((item, _) => {
      armorTokenIdArray.push(item.armorTokenId);
      helmetTokenIdArray.push(item.helmetTokenId);
      shieldTokenIdArray.push(item.shieldTokenId);
      weaponTokenIdArray.push(item.weaponTokenId);
    });

    const normalItemsCollections = await CollectionQuery.fetchNormalItems(
      armorTokenIdArray,
      helmetTokenIdArray,
      shieldTokenIdArray,
      weaponTokenIdArray
    );
    const temp = {};
    counter = 0;
    for (const collection of collectionDataList) {
      temp[collection.id] = { ...collection, ...normalItemsCollections[counter] };
      counter++;
    }
    counter = 0;
    for (const _battle of battleInfo) {
      const _id = parseInt(armyIds[counter]);
      const battle = _battle[0];
      const stakedKnightId = battle.stakedKnight.map((item) => parseInt(item));
      result[_id]["stakedKnight"] = [];
      for (const knightId of stakedKnightId) result[_id]["stakedKnight"].push(temp[knightId]);
      counter++;
    }
    for (const _id of armyIds) {
      result[_id]["stakedKnight"] = result[_id]["stakedKnight"].sort(collectionSort);
    }
    return result;
  }

  static async fetchDetailArmy(armyId) {
    const battleInfo = await DilutionStakingContract.getArmyInfo(armyId);
    const stakedKnightId = battleInfo.stakedKnight.map((item) => parseInt(item));
    const { normalCollections } = await CollectionQuery.fetchBasicCollections(stakedKnightId);
    let armorTokenIdArray = [];
    let helmetTokenIdArray = [];
    let shieldTokenIdArray = [];
    let weaponTokenIdArray = [];
    normalCollections.forEach((item, _) => {
      armorTokenIdArray.push(item.armorTokenId);
      helmetTokenIdArray.push(item.helmetTokenId);
      shieldTokenIdArray.push(item.shieldTokenId);
      weaponTokenIdArray.push(item.weaponTokenId);
    });
    const normalItemsCollections = await CollectionQuery.fetchNormalItems(
      armorTokenIdArray,
      helmetTokenIdArray,
      shieldTokenIdArray,
      weaponTokenIdArray
    );
    const temp = [];
    let counter = 0;
    for (const collection of normalCollections) {
      temp.push({ ...collection, ...normalItemsCollections[counter] });
      counter++;
    }
    const armyRank = parseInt(battleInfo.armyRank);
    const type = DilutionRank[armyRank];
    const dilution = parseInt(battleInfo.dilution) / 10000;
    const metadata = TimeLockPercentage.find((item) => item.percent == dilution) ?? TimeLockPercentage[0];
    const unlockTime = parseInt(battleInfo.unlockTime);
    const lockTime = unlockTime - metadata["date"];
    const lockAmount = parseInt(BigNumber(battleInfo.lockAmount).div(BASE18));
    const result = {
      armyRank,
      type,
      metadata,
      dilution,
      lockAmount,
      reward: lockAmount * metadata["percent"],
      lockCirculatingSupply: parseInt(BigNumber(battleInfo.lockCirculatingSupply).div(BASE18)),
      unlockCirculatingSupply: parseInt(BigNumber(battleInfo.unlockCirculatingSupply).div(BASE18)),
      lockTime,
      unlockTime,
      id: parseInt(armyId),
      stakedKnight: temp.sort(collectionSort),
    };
    return result;
  }

  static async fetchLimitedArmy() {
    const len = await DilutionStakingContract.privateBattleFieldId();
    const [armyInfos, detailInfos] = await Promise.all([
      multiCall(
        DilutionStakingContract.ABI,
        new Array(parseInt(len)).fill(1).map((_, index) => ({
          address: DilutionStakingContract.ADDRESS,
          name: "privateBattleInfos",
          params: [index + 1],
        }))
      ),
      multiCall(
        DilutionStakingContract.ABI,
        new Array(parseInt(len)).fill(1).map((_, index) => ({
          address: DilutionStakingContract.ADDRESS,
          name: "privateBattleStates",
          params: [index + 1],
        }))
      ),
    ]);
    const result = {};
    let counter = 0;
    for (const army of armyInfos) {
      const _id = parseInt(army.tokenId);
      if (!isNullAddress(army.owner)) {
        const detailData = detailInfos[counter];
        const totalPower = parseInt(detailData.totalPower._hex) / 100;
        const dilutionProjection = 100 - 0.04 * totalPower;
        result[_id] = {
          id: _id,
          privateBattleId: counter + 1,
          owner: army.owner,
          lockAmount: parseInt(BigNumber(army.lockAmount._hex).div(BASE18)),
          lockCirculatingSupply: parseInt(BigNumber(army.lockCirculatingSupply._hex).div(BASE18)),
          index: parseInt(detailData.index._hex),
          unlockTime: parseInt(detailData.unlockTime._hex),
          lastUpdate: parseInt(detailData.lastUpdate._hex),
          totalPower: parseInt(detailData.totalPower._hex) / 100,
          dilutionProjection: dilutionProjection / 100,
          totalFee: parseInt(detailData.totalFee._hex),
          unlockCirculatingSupply: parseInt(detailData.unlockCirculatingSupply._hex),
        };
      }
      counter++;
    }
    return result;
  }
}
